/**
 * useBuildingHierarchy — builds a tree from rooms + the configured building /
 * floor hierarchy.
 *
 * Result: buildings[] → floors[] → rooms[], ordered the way GRMS §4.4 requires:
 * buildings by `sortOrder` then `buildingCode`, floors by `sortOrder` then
 * `floorCode`, both read from the REST hierarchy
 * (`GET /buildings`, then each building's `/floors`) rather than inferred from
 * the shape of a floor code. Rooms carry no `floorSortOrder` contract, so a
 * group the hierarchy does not describe follows the configured ones, ordered by
 * its exact code pair with a null code last — and an unavailable hierarchy is
 * reported as an ordering failure instead of being passed off as configured
 * order.
 *
 * Optionally filters by search string and visibility of empty floors.
 */
import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { queryKeys, useBuildings, useRooms } from './useApi.js';
import { getApiClient } from '../api/client.js';
import { useAuthStore } from '../store/authStore.js';
import { floorScopeKey } from '../utils/floorPlan.js';
export const EMPTY_FLOOR_ORDER = {
    rank: new Map(),
    buildingNames: new Map(),
    floorNames: new Map(),
    buildingSortOrder: new Map(),
    floorSortOrder: new Map(),
    configured: false,
};
// =============================================================================
// Ordering (pure — the display rule of GRMS §4.4, without React)
// =============================================================================
/** Split a `floorScopeKey` back into its building and floor codes. */
function splitFloorScopeKey(key) {
    const separator = key.indexOf('\u0000');
    if (separator < 0)
        return ['', key];
    return [key.slice(0, separator), key.slice(separator + 1)];
}
/** Ascending, deterministic, locale-independent; an empty code sorts last. */
function compareCode(a, b) {
    if (a === b)
        return 0;
    if (a === '')
        return 1;
    if (b === '')
        return -1;
    return a < b ? -1 : 1;
}
function compareBySortOrderThenCode(aSort, aCode, bSort, bCode) {
    if (aSort !== bSort)
        return aSort - bSort;
    return compareCode(aCode, bCode);
}
/**
 * Flatten the hierarchy into the configured floor order: buildings by
 * `sortOrder` then `buildingCode`, floors by `sortOrder` then `floorCode`.
 */
export function buildFloorOrder(buildings, floorsByBuilding, configured = true) {
    const rank = new Map();
    const buildingNames = new Map();
    const floorNames = new Map();
    const buildingSortOrder = new Map();
    const floorSortOrder = new Map();
    const orderedBuildings = [...buildings].sort((a, b) => compareBySortOrderThenCode(a.sortOrder ?? 0, a.buildingCode, b.sortOrder ?? 0, b.buildingCode));
    let position = 0;
    for (const building of orderedBuildings) {
        buildingNames.set(building.buildingCode, building.buildingName);
        buildingSortOrder.set(building.buildingCode, building.sortOrder ?? 0);
        const floors = [...(floorsByBuilding.get(building.buildingCode) ?? [])].sort((a, b) => compareBySortOrderThenCode(a.sortOrder ?? 0, a.floorCode, b.sortOrder ?? 0, b.floorCode));
        for (const floor of floors) {
            const key = floorScopeKey(building.buildingCode, floor.floorCode);
            if (rank.has(key))
                continue;
            rank.set(key, position++);
            floorNames.set(key, floor.floorName);
            floorSortOrder.set(key, floor.sortOrder ?? 0);
        }
    }
    return { rank, buildingNames, floorNames, buildingSortOrder, floorSortOrder, configured };
}
/**
 * Order two floor groups. Configured floors come first in their configured
 * order; the rest follow, ordered by their exact `buildingCode`/`floorCode`
 * pair with a missing code last (GRMS §4.4).
 */
export function compareFloorScopeKeys(a, b, index) {
    if (a === b)
        return 0;
    const rankA = index.rank.get(a);
    const rankB = index.rank.get(b);
    if (rankA !== undefined && rankB !== undefined)
        return rankA - rankB;
    if (rankA !== undefined)
        return -1;
    if (rankB !== undefined)
        return 1;
    const [buildingA, floorA] = splitFloorScopeKey(a);
    const [buildingB, floorB] = splitFloorScopeKey(b);
    const byBuilding = compareCode(buildingA, buildingB);
    return byBuilding !== 0 ? byBuilding : compareCode(floorA, floorB);
}
// =============================================================================
// Hooks
// =============================================================================
function combineFloorQueries(results) {
    return {
        data: results.map((query) => query.data ?? []),
        isLoading: results.some((query) => query.isLoading),
        isError: results.some((query) => query.isError),
        isSuccess: results.every((query) => query.isSuccess),
    };
}
/**
 * Fetch the configured building/floor order. Requires `buildings:read` and the
 * `full` profile, so simple-profile callers pass `enabled: false` and get the
 * empty index rather than a 400 per building.
 */
export function useFloorOrder(options = {}) {
    const { enabled = true } = options;
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    const active = enabled && !!siteCode;
    const { data: buildings = [], isLoading: buildingsLoading, isError: buildingsError, isSuccess: buildingsLoaded, } = useBuildings({ enabled: active });
    // One `/floors` call per building — floors are not embedded in the buildings
    // payload (REST, Building hierarchy). The shared client is already carrying
    // the auth-store getters by the time a floors query exists, because the
    // buildings query that produced this list installed them.
    const { data: floorData, isLoading: floorsLoading, isError: floorsError, isSuccess: floorsLoaded, } = useQueries({
        queries: buildings.map((building) => ({
            queryKey: queryKeys.floors(siteCode ?? '', building.buildingCode),
            queryFn: () => getApiClient().getFloors(building.buildingCode),
            enabled: active,
        })),
        combine: combineFloorQueries,
    });
    const isLoading = active && (buildingsLoading || floorsLoading);
    const isError = active && (buildingsError || floorsError);
    const configured = active && buildingsLoaded && floorsLoaded;
    // `combineFloorQueries` is stable, so `useQueries` structurally shares its
    // result: `floorData` keeps its identity until a response changes, and the
    // index is rebuilt then, not per render.
    const index = useMemo(() => {
        if (!configured)
            return EMPTY_FLOOR_ORDER;
        const floorsByBuilding = new Map();
        buildings.forEach((building, i) => {
            floorsByBuilding.set(building.buildingCode, floorData[i] ?? []);
        });
        return buildFloorOrder(buildings, floorsByBuilding);
    }, [configured, buildings, floorData]);
    return {
        index,
        isLoading,
        /** The hierarchy could not be read, so the displayed order is not the configured one. */
        isOrderingUnavailable: isError,
        configured,
    };
}
export function useBuildingHierarchy(options = {}) {
    const { search, showEmptyFloors = false, enabled = true } = options;
    const { data: rooms = [], isLoading: roomsLoading } = useRooms();
    const { data: buildings = [], isLoading: buildingsLoading } = useBuildings({ enabled });
    const { index: floorOrder, isLoading: floorsLoading, isOrderingUnavailable } = useFloorOrder({ enabled });
    const isLoading = roomsLoading || buildingsLoading || floorsLoading;
    const data = useMemo(() => {
        // Apply search filter
        let filtered = rooms;
        if (search?.trim()) {
            const q = search.toLowerCase();
            filtered = rooms.filter((r) => r.roomName.toLowerCase().includes(q) ||
                r.roomCode.toLowerCase().includes(q) ||
                r.buildingCode?.toLowerCase().includes(q) ||
                r.floorCode?.toLowerCase().includes(q));
        }
        // Group rooms by building → floor
        const buildingMap = new Map();
        const unassigned = [];
        for (const room of filtered) {
            if (!room.buildingCode || !room.floorCode) {
                unassigned.push(room);
                continue;
            }
            let floorMap = buildingMap.get(room.buildingCode);
            if (!floorMap) {
                floorMap = new Map();
                buildingMap.set(room.buildingCode, floorMap);
            }
            let floorRooms = floorMap.get(room.floorCode);
            if (!floorRooms) {
                floorRooms = [];
                floorMap.set(room.floorCode, floorRooms);
            }
            floorRooms.push(room);
        }
        // Build sorted tree from buildings data
        const buildingLookup = new Map();
        for (const b of buildings) {
            buildingLookup.set(b.buildingCode, b);
        }
        // Collect all building codes (from buildings list + rooms references)
        const allBuildingCodes = new Set([
            ...buildings.map((b) => b.buildingCode),
            ...buildingMap.keys(),
        ]);
        const buildingNodes = [];
        for (const code of allBuildingCodes) {
            const buildingInfo = buildingLookup.get(code);
            const floorMap = buildingMap.get(code);
            // Collect all floor codes for this building
            const floorCodes = new Set();
            if (floorMap) {
                for (const fc of floorMap.keys())
                    floorCodes.add(fc);
            }
            if (showEmptyFloors) {
                // Configured floors that hold no matching room, plus floors that only
                // rooms outside the current search know about.
                for (const key of floorOrder.rank.keys()) {
                    const [buildingCode, floorCode] = splitFloorScopeKey(key);
                    if (buildingCode === code && floorCode)
                        floorCodes.add(floorCode);
                }
                for (const room of rooms) {
                    if (room.buildingCode === code && room.floorCode) {
                        floorCodes.add(room.floorCode);
                    }
                }
            }
            const floors = [];
            for (const fc of floorCodes) {
                const floorRooms = floorMap?.get(fc) ?? [];
                if (!showEmptyFloors && floorRooms.length === 0)
                    continue;
                const scopeKey = floorScopeKey(code, fc);
                // The configured name wins; a room's own floorName is the fallback.
                const floorName = floorOrder.floorNames.get(scopeKey)
                    ?? floorRooms[0]?.floorName
                    ?? rooms.find((r) => r.buildingCode === code && r.floorCode === fc)?.floorName
                    ?? fc;
                floors.push({
                    floorCode: fc,
                    floorName,
                    sortOrder: floorOrder.floorSortOrder.get(scopeKey) ?? 0,
                    rooms: floorRooms,
                    channelCount: 0, // Room list API doesn't include per-room channel counts
                });
            }
            floors.sort((a, b) => compareFloorScopeKeys(floorScopeKey(code, a.floorCode), floorScopeKey(code, b.floorCode), floorOrder));
            // Skip empty buildings entirely (unless showEmptyFloors and building exists in buildings list)
            if (floors.length === 0 && !showEmptyFloors)
                continue;
            if (floors.length === 0 && showEmptyFloors && !buildingInfo)
                continue;
            const roomCount = floors.reduce((sum, f) => sum + f.rooms.length, 0);
            buildingNodes.push({
                buildingCode: code,
                buildingName: floorOrder.buildingNames.get(code) ?? buildingInfo?.buildingName ?? code,
                sortOrder: floorOrder.buildingSortOrder.get(code) ?? buildingInfo?.sortOrder ?? 0,
                floors,
                roomCount,
            });
        }
        // A building the hierarchy does not describe follows the configured ones,
        // ordered by its exact code (§4.4).
        buildingNodes.sort((a, b) => {
            const knownA = floorOrder.buildingSortOrder.has(a.buildingCode);
            const knownB = floorOrder.buildingSortOrder.has(b.buildingCode);
            if (knownA !== knownB)
                return knownA ? -1 : 1;
            return compareBySortOrderThenCode(a.sortOrder, a.buildingCode, b.sortOrder, b.buildingCode);
        });
        return {
            buildings: buildingNodes,
            unassigned,
            totalRooms: filtered.length,
        };
    }, [rooms, buildings, search, showEmptyFloors, floorOrder]);
    return { data, isLoading, isOrderingUnavailable };
}
//# sourceMappingURL=useBuildingHierarchy.js.map